import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { ArrowLeft, ArrowRight, ArrowUpRight, ExternalLink } from 'lucide-react';
import { projects, projectOrder } from '../data/projects';
import { SEO } from '../components/SEO';
import { AnimatedMetric } from '../components/AnimatedMetric';
import { 
  BorsamexCircuit,
  DatexTraderCircuit, 
  IzmexCircuit,
  IzmexCarbonCircuit, 
  IzmexMediaCircuit,
} from '../components/hero-circuits';

// Map project IDs to their circuit components
const circuitComponents = {
  'borsamex': BorsamexCircuit,       // Crypto signal pipeline schematic
  'datextrader': DatexTraderCircuit, // Order routing schematic
  'izmex': IzmexCircuit,             // B2B marketplace schematic
  'izmexcarbon': IzmexCarbonCircuit, // Data lake schematic
  'izmexmedia': IzmexMediaCircuit,   // CDN pipeline schematic
};

export function ProjectDetailPage() {
  const { projectId } = useParams();
  const { currentLanguage } = useLanguage();
  const project = projects[projectId];

  const labels = {
    en: {
      back: 'All Projects',
      overview: 'Project Overview',
      industry: 'Industry',
      systemType: 'System Type',
      year: 'Year',
      problem: 'The Problem',
      context: 'Business Context',
      constraints: 'Technical Constraints',
      requirements: 'Performance Requirements',
      approach: 'Engineering Approach',
      architecture: 'Architecture Decisions',
      tradeoffs: 'Trade-offs',
      scalability: 'Scalability Design',
      systemArch: 'System Architecture',
      stack: 'Technology Stack',
      infrastructure: 'Infrastructure',
      dataFlow: 'Data Flow',
      performance: 'Performance & Reliability',
      impact: 'Impact',
      business: 'Business Outcome',
      stability: 'Operational Stability',
      strategic: 'Strategic Value',
      visitSite: 'Visit Live Site',
      nextProject: 'Next Project'
    },
    tr: {
      back: 'Tüm Projeler',
      overview: 'Proje Özeti',
      industry: 'Sektör',
      systemType: 'Sistem Tipi',
      year: 'Yıl',
      problem: 'Problem',
      context: 'İş Bağlamı',
      constraints: 'Teknik Kısıtlamalar',
      requirements: 'Performans Gereksinimleri',
      approach: 'Mühendislik Yaklaşımı',
      architecture: 'Mimari Kararlar',
      tradeoffs: 'Trade-off\'lar',
      scalability: 'Ölçeklenebilirlik Tasarımı',
      systemArch: 'Sistem Mimarisi',
      stack: 'Teknoloji Stack',
      infrastructure: 'Altyapı',
      dataFlow: 'Veri Akışı',
      performance: 'Performans & Güvenilirlik',
      impact: 'Etki',
      business: 'İş Sonucu',
      stability: 'Operasyonel Stabilite',
      strategic: 'Stratejik Değer',
      visitSite: 'Canlı Siteyi Ziyaret Et',
      nextProject: 'Sonraki Proje'
    },
    de: {
      back: 'Alle Projekte',
      overview: 'Projektübersicht',
      industry: 'Branche',
      systemType: 'Systemtyp',
      year: 'Jahr',
      problem: 'Das Problem',
      context: 'Geschäftskontext',
      constraints: 'Technische Einschränkungen',
      requirements: 'Performance-Anforderungen',
      approach: 'Engineering-Ansatz',
      architecture: 'Architekturentscheidungen',
      tradeoffs: 'Trade-offs',
      scalability: 'Skalierbarkeitsdesign',
      systemArch: 'Systemarchitektur',
      stack: 'Technologie-Stack',
      infrastructure: 'Infrastruktur',
      dataFlow: 'Datenfluss',
      performance: 'Performance & Zuverlässigkeit',
      impact: 'Auswirkung',
      business: 'Geschäftsergebnis',
      stability: 'Betriebsstabilität',
      strategic: 'Strategischer Wert',
      visitSite: 'Live-Site besuchen',
      nextProject: 'Nächstes Projekt'
    }
  };

  const t = labels[currentLanguage] || labels.en;

  if (!project) {
    return <Navigate to="/case-studies" replace />;
  }

  // Get next project
  const currentIndex = projectOrder.indexOf(projectId);
  const nextIndex = (currentIndex + 1) % projectOrder.length;
  const nextProjectId = projectOrder[nextIndex];
  const nextProject = projects[nextProjectId];

  const getText = (obj) => obj?.[currentLanguage] || obj?.en || '';

  return (
    <>
      <SEO 
        title={project.name}
        description={getText(project.tagline)}
      />
      
      <div className="min-h-screen pt-28 pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          
          {/* Back Link */}
          <Link 
            to="/case-studies"
            className="inline-flex items-center gap-2 text-sm mb-10 transition-colors hover:text-[#0F766E]"
            style={{ color: 'var(--text-on-light-muted)' }}
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          {/* Project Header */}
          <header className="mb-16">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              {/* Left: Text Content */}
              <div className="flex-1">
                <div className="flex items-center gap-4 mb-4">
                  <span 
                    className="text-xs uppercase tracking-widest px-2 py-1 rounded"
                    style={{ 
                      background: 'rgba(15, 118, 110, 0.1)',
                      color: '#0F766E'
                    }}
                  >
                    {getText(project.category)}
                  </span>
                  <span 
                    className="text-xs"
                    style={{ color: 'var(--text-on-light-subtle)', fontFamily: 'JetBrains Mono, monospace' }}
                  >
                    {project.year}
                  </span>
                </div>
                
                <h1 
                  className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight mb-4"
                  style={{ color: 'var(--text-on-light-primary)' }}
                >
                  {project.name}
                </h1>
                
                <p 
                  className="text-lg md:text-xl mb-6"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.tagline)}
                </p>

                {project.link && (
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all hover:scale-105 hover:opacity-90"
                    style={{ 
                      background: 'linear-gradient(135deg, #0F766E 0%, #14B8A6 100%)',
                      color: '#FFFFFF',
                      border: '1px solid rgba(15, 118, 110, 0.3)'
                    }}
                  >
                    {t.visitSite}
                    <ArrowUpRight className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Right: Hero Circuit - Project-specific topology */}
              {circuitComponents[project.id] && (
                <div className="hidden lg:flex items-center justify-center">
                  {React.createElement(circuitComponents[project.id])}
                </div>
              )}
            </div>
          </header>

          {/* Performance Metrics Bar - Animated */}
          <div 
            className="grid grid-cols-2 md:grid-cols-4 gap-4 p-6 rounded-xl mb-16"
            style={{ 
              background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
              border: '1px solid rgba(15, 118, 110, 0.15)'
            }}
          >
            {project.performance && Object.entries(project.performance).map(([key, item], idx) => (
              <AnimatedMetric
                key={key}
                value={item.value}
                label={getText(item.label)}
                duration={1000 + (idx * 150)}
                showNote={idx === 0}
              />
            ))}
          </div>

          {/* THE PROBLEM */}
          <section className="mb-16">
            <h2 
              className="text-xs uppercase tracking-widest mb-6"
              style={{ color: '#0F766E' }}
            >
              {t.problem}
            </h2>
            
            <div className="space-y-6">
              <div>
                <h3 
                  className="text-sm font-medium mb-2"
                  style={{ color: 'var(--text-on-light-secondary)' }}
                >
                  {t.context}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.problem?.context)}
                </p>
              </div>
              
              <div>
                <h3 
                  className="text-sm font-medium mb-2"
                  style={{ color: 'var(--text-on-light-secondary)' }}
                >
                  {t.constraints}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.problem?.constraints)}
                </p>
              </div>
              
              <div>
                <h3 
                  className="text-sm font-medium mb-2"
                  style={{ color: 'var(--text-on-light-secondary)' }}
                >
                  {t.requirements}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.problem?.requirements)}
                </p>
              </div>
            </div>
          </section>

          {/* ENGINEERING APPROACH */}
          <section className="mb-16">
            <h2 
              className="text-xs uppercase tracking-widest mb-6"
              style={{ color: '#0F766E' }}
            >
              {t.approach}
            </h2>
            
            <div className="space-y-6">
              <div 
                className="p-6 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-sm font-medium mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.architecture}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.approach?.architecture)}
                </p>
              </div>
              
              <div 
                className="p-6 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-sm font-medium mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.tradeoffs}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.approach?.tradeoffs)}
                </p>
              </div>
              
              <div 
                className="p-6 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-sm font-medium mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.scalability}
                </h3>
                <p 
                  className="text-base leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.approach?.scalability)}
                </p>
              </div>
            </div>
          </section>

          {/* SYSTEM ARCHITECTURE - Block Diagram */}
          <section className="mb-16">
            <h2 
              className="text-xs uppercase tracking-widest mb-8"
              style={{ color: '#0F766E', letterSpacing: '0.15em' }}
            >
              {t.systemArch}
            </h2>
            
            {/* Architecture Block Diagram */}
            {project.architectureLayers && (
              <div 
                className="p-6 md:p-8 rounded-xl mb-6"
                style={{ 
                  background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.08) 0%, rgba(15, 118, 110, 0.02) 100%)',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <div className="space-y-0">
                  {project.architectureLayers.map((layer, idx) => {
                    const isLast = idx === project.architectureLayers.length - 1;
                    const isHeavy = layer.color === 'heavy';
                    
                    return (
                      <div key={layer.id}>
                        {/* Layer Block */}
                        <div 
                          className="group relative p-4 md:p-5 rounded-lg transition-all duration-300 hover:bg-[rgba(15,118,110,0.08)]"
                          style={{ 
                            background: isHeavy 
                              ? 'linear-gradient(135deg, rgba(15, 118, 110, 0.15) 0%, rgba(15, 118, 110, 0.05) 100%)'
                              : 'rgba(15, 118, 110, 0.04)',
                            border: isHeavy 
                              ? '1px solid rgba(15, 118, 110, 0.2)'
                              : '1px solid rgba(15, 118, 110, 0.08)'
                          }}
                        >
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                            {/* Layer Name */}
                            <h4 
                              className="text-xs uppercase tracking-wider font-medium"
                              style={{ 
                                color: isHeavy ? '#0F766E' : 'var(--text-on-light-secondary)',
                                letterSpacing: '0.1em'
                              }}
                            >
                              {getText(layer.name)}
                            </h4>
                            
                            {/* Components */}
                            <p 
                              className="text-xs"
                              style={{ 
                                color: 'var(--text-on-light-muted)',
                                fontFamily: 'JetBrains Mono, monospace'
                              }}
                            >
                              {layer.components}
                            </p>
                          </div>
                        </div>
                        
                        {/* Connector Line */}
                        {!isLast && (
                          <div className="flex justify-center py-1">
                            <div 
                              className="w-px h-4"
                              style={{ background: 'rgba(15, 118, 110, 0.15)' }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Performance Metrics Strip */}
                {project.performance && (
                  <div 
                    className="mt-6 pt-5 flex flex-wrap items-center justify-between gap-4"
                    style={{ borderTop: '1px solid rgba(15, 118, 110, 0.1)' }}
                  >
                    <div className="flex flex-wrap items-center gap-6">
                      {Object.entries(project.performance).slice(0, 3).map(([key, item]) => (
                        <div key={key} className="flex items-baseline gap-2">
                          <span 
                            className="text-xs"
                            style={{ color: 'var(--text-on-light-muted)' }}
                          >
                            {getText(item.label)}:
                          </span>
                          <span 
                            className="text-sm font-medium"
                            style={{ color: '#0F766E', fontFamily: 'JetBrains Mono, monospace' }}
                          >
                            {item.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Operational Status */}
                    <div 
                      className="flex items-center gap-2 px-3 py-1.5 rounded"
                      style={{ background: 'rgba(46, 125, 50, 0.12)' }}
                    >
                      <span 
                        className="w-1.5 h-1.5 rounded-full animate-pulse"
                        style={{ background: '#2E7D32' }}
                      />
                      <span 
                        className="text-[10px] uppercase tracking-wider font-medium"
                        style={{ color: '#2E7D32' }}
                      >
                        Production
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Tech Stack & Details */}
            <div 
              className="p-6 rounded-xl space-y-6"
              style={{ 
                background: '#F8FAFB',
                border: '1px solid rgba(15, 118, 110, 0.08)'
              }}
            >
              {/* Tech Stack */}
              <div>
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: 'var(--text-on-light-subtle)', letterSpacing: '0.1em' }}
                >
                  {t.stack}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {project.stack?.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1.5 rounded text-sm"
                      style={{ 
                        background: 'rgba(15, 118, 110, 0.1)',
                        border: '1px solid rgba(15, 118, 110, 0.15)',
                        color: '#0F766E',
                        fontFamily: 'JetBrains Mono, monospace'
                      }}
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* Infrastructure */}
              <div>
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: 'var(--text-on-light-subtle)', letterSpacing: '0.1em' }}
                >
                  {t.infrastructure}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.infrastructure)}
                </p>
              </div>

              {/* Data Flow */}
              <div>
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: 'var(--text-on-light-subtle)', letterSpacing: '0.1em' }}
                >
                  {t.dataFlow}
                </h3>
                <p 
                  className="text-sm leading-relaxed font-mono"
                  style={{ color: 'var(--text-on-light-secondary)' }}
                >
                  {getText(project.dataFlow)}
                </p>
              </div>
            </div>
          </section>

          {/* IMPACT */}
          <section className="mb-16">
            <h2 
              className="text-xs uppercase tracking-widest mb-6"
              style={{ color: '#0F766E' }}
            >
              {t.impact}
            </h2>
            
            <div className="grid md:grid-cols-3 gap-4">
              <div 
                className="p-5 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.business}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.impact?.business)}
                </p>
              </div>
              
              <div 
                className="p-5 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.stability}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.impact?.stability)}
                </p>
              </div>
              
              <div 
                className="p-5 rounded-xl"
                style={{ 
                  background: '#F8FAFB',
                  border: '1px solid rgba(15, 118, 110, 0.1)'
                }}
              >
                <h3 
                  className="text-xs uppercase tracking-wider mb-3"
                  style={{ color: '#0F766E' }}
                >
                  {t.strategic}
                </h3>
                <p 
                  className="text-sm leading-relaxed"
                  style={{ color: 'var(--text-on-light-muted)' }}
                >
                  {getText(project.impact?.strategic)}
                </p>
              </div>
            </div>
          </section>

          {/* Next Project */}
          <section className="pt-10 border-t" style={{ borderColor: 'rgba(15, 118, 110, 0.1)' }}>
            <Link
              to={`/case-studies/${nextProjectId}`}
              className="group block p-6 rounded-xl transition-all hover:border-[#0F766E]"
              style={{ 
                background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.05) 0%, rgba(15, 118, 110, 0.01) 100%)',
                border: '1px solid rgba(15, 118, 110, 0.15)'
              }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <span 
                    className="text-xs uppercase tracking-widest mb-2 block"
                    style={{ color: 'var(--text-on-light-subtle)' }}
                  >
                    {t.nextProject}
                  </span>
                  <span 
                    className="text-xl font-semibold group-hover:text-[#0F766E] transition-colors"
                    style={{ color: 'var(--text-on-light-primary)' }}
                  >
                    {nextProject.name}
                  </span>
                  <span 
                    className="text-sm block mt-1"
                    style={{ color: 'var(--text-on-light-muted)' }}
                  >
                    {getText(nextProject.category)}
                  </span>
                </div>
                <ArrowRight 
                  className="w-6 h-6 group-hover:translate-x-2 transition-transform" 
                  style={{ color: '#0F766E' }}
                />
              </div>
            </Link>
          </section>

        </div>
      </div>
    </>
  );
}
